/** Nyafaz portal release notes — keep in sync with docs/CHANGELOG.md and git tags. */

export const CURRENT_VERSION = "1.15";

export const RELEASE_NOTES = [
  {
    version: "1.15",
    date: "2026-09-01",
    title: "Hair Insight salon report, CV scoring, and face identity on report",
    sections: [
      {
        heading: "Added",
        items: [
          "Hair Insight advanced flow: overview + three USB scalp zones with annotated report and PDF.",
          "Fast CV-based coverage and per-zone microscope scoring for kiosk performance.",
          "Customer card on Hair Insight report from face identity check-in.",
        ],
      },
      {
        heading: "Changed",
        items: [
          "Report UI deduplicated; numeric zone scores and restored radar profile chart.",
          "Naturals hair flow Page Setup background; wide capture flip and retake fixes.",
        ],
      },
    ],
  },
  {
    version: "1.12",
    date: "2026-08-24",
    title: "Salon reel quality, bundled fonts, and unified loader",
    sections: [
      {
        heading: "Added",
        items: [
          "Qwen-VL AI reel director with motion-aware cuts and per-render style packs.",
          "Varied transitions, music beds, and SFX so each render feels unique.",
          "Bundled Poppins fonts for reel overlays (no Windows/system font paths).",
          "Single Creating your reel screen from upload through preview.",
          "Mobile and tablet layout for the Naturals salon video flow.",
        ],
      },
      {
        heading: "Fixed",
        items: [
          "Final reels no longer show BEFORE, AFTER, PROCESS, or service name labels.",
          "Sharper 9:16 encode and engagement-only overlay text.",
          "Blurry output and same transition/audio on every render.",
        ],
      },
      {
        heading: "Changed",
        items: [
          "Reels target 30–45s with three default process cuts.",
          "Naturals video UI uses brand purple/gold on cream.",
        ],
      },
    ],
  },
  {
    version: "1.11",
    date: "2026-08-22",
    title: "FaceMesh detection restored (skin, salon, Naturals)",
    sections: [
      {
        heading: "Fixed",
        items: [
          "Stable CDN FaceProcessor and camera bootstrap aligned with Naturals flows.",
          "Face-identity video binding corrected across skin and salon capture.",
        ],
      },
    ],
  },
  {
    version: "1.10",
    date: "2026-08-20",
    title: "Salon video UI in main frontend",
    sections: [
      {
        heading: "Changed",
        items: [
          "Salon video flow moved into frontend/src/nyafaz-ep/video (CRA app).",
          "Nginx and dev proxy wired for /api/salon-video.",
        ],
      },
    ],
  },
  {
    version: "1.9",
    date: "2026-08-18",
    title: "Skin Expert QR chat, catalog products, and multi-agent AI",
    sections: [
      {
        heading: "Added",
        items: [
          "Skin Expert QR: consent, photo capture, quality check, Qwen3-VL analysis, then Nemotron chat.",
          "Chat product and treatment cards from the workspace catalog (not a live scrape).",
          "Dedicated AI agents (skin analysis, Skin AI Chat QR, salon, makeup, hair coloring) with separate RAG and queues.",
          "Direct answers for scan recap, severity ranking, tips, and “more products”.",
        ],
      },
      {
        heading: "Fixed",
        items: [
          "Skin QR capture no longer rejects usable photos as too blurry.",
          "Analysis no longer sticks on “analyzing” when the vision model returns empty JSON.",
        ],
      },
      {
        heading: "Changed",
        items: [
          "Skin Expert chat uses the org logo; franchise/outlet names are not shown in chat.",
        ],
      },
    ],
  },
  {
    version: "1.8",
    date: "2026-08-14",
    title: "Security hardening and operational PII encryption",
    sections: [
      {
        heading: "Security",
        items: [
          "Audit score 92 / 100: DPDP erasure, ops PII encryption, portal cookie-only auth, and login rate limits.",
          "Check-in queue and placed-order PII encrypted at rest.",
        ],
      },
    ],
  },
  {
    version: "1.7",
    date: "2026-08-13",
    title: "Portal auth, activation redirect, and AI chat permissions",
    sections: [
      {
        heading: "Fixed",
        items: [
          "Set-password and email activation now redirect to the correct portal (Executive, Workspace, or Partner).",
          "Nyafaz Portal login stays signed in with HttpOnly session cookies.",
          "Naturals backend boot on production (import path and ONNX CUDA guard).",
        ],
      },
      {
        heading: "Added",
        items: [
          "AI Chat & Agents as a separate admin permission in Executive admin access control.",
        ],
      },
      {
        heading: "Changed",
        items: [
          "Workspace AI agents menu and APIs use the new manage_ai_chat permission (legacy skin-settings access still works).",
        ],
      },
    ],
  },
  {
    version: "1.6",
    date: "2026-08-13",
    title: "Security hardening and session storage",
    sections: [
      {
        heading: "Security",
        items: [
          "Removed NXTFace and NXTFace Admin surfaces; legacy naturals_nxtface storage purged on bootstrap.",
          "Kiosk auth is cookie-only with JWT jti revocation (logout and geofence sign-out).",
          "Guest PII, face session, visit cache, flow state, and pending analysis saves moved to in-memory stores.",
          "Per-org APK bootstrap tokens; server geofence on salon and makeup write APIs for franchise sessions.",
        ],
      },
      {
        heading: "Changed",
        items: [
          "Makeup Studio and salon flows use in-memory session getters instead of sessionStorage.",
        ],
      },
    ],
  },
  {
    version: "1.5",
    date: "2026-08-13",
    title: "Kiosk preview and AI agent layout fixes",
    sections: [
      {
        heading: "Fixed",
        items: [
          "Kiosk builder Open live preview opens as a centered full-screen modal on tablet.",
          "AI agent Save settings button aligned in the page header on desktop and mobile.",
        ],
      },
    ],
  },
  {
    version: "1.4",
    date: "2026-08-12",
    title: "Session restore and Workspace polish",
    sections: [
      {
        heading: "Fixed",
        items: [
          "HQ portals no longer log out on page refresh.",
          "Session restore now sends the portal header so the correct access cookie is used.",
          "Logo load or network blips no longer wipe a valid session.",
          "Setup guide text contrast improved on light and dark pages.",
        ],
      },
      {
        heading: "Changed",
        items: [
          "Landing, registration, and terms cards use clearer titles and hide internal route/CTA metadata.",
          "In-app Release notes page available from the sidebar version label.",
        ],
      },
    ],
  },
  {
    version: "1.3",
    date: "2026-08-12",
    title: "HQ portal UI polish",
    sections: [
      {
        heading: "Added",
        items: [
          "Shared portal theme layer (`portal-ui.css`) for Executive, Workspace, and Partner.",
          "Workspace setup guides (`ConfigGuide`) on configuration pages.",
          "AI chat sample knowledge files and widget settings under `docs/samples/ai-chat`.",
          "In-app Release notes page for Executive, Workspace, and Partner.",
        ],
      },
      {
        heading: "Changed",
        items: [
          "Professional nav and page titles across Executive, Workspace, and Partner.",
          "Softer plum brand accents; replaced cyan dashboard highlights.",
          "Login headings: Sign in to Executive / Workspace / Partner.",
          "Clearer Workspace guide copy for kiosk, catalog, WhatsApp, and franchise users.",
        ],
      },
    ],
  },
  {
    version: "1.2",
    date: "2026-08-12",
    title: "Isolated HQ portals",
    sections: [
      {
        heading: "Added",
        items: [
          "Separate Executive, Workspace, and Partner apps with isolated sessions.",
          "Prefixed storage (`naturals_executive_*`, `naturals_workspace_*`, `naturals_partner_*`).",
          "Portal-specific auth cookies and `X-Naturals-Portal` header selection.",
          "Login routes: `/nyafaz-ep/executive`, `/nyafaz-ep/workspace`, `/nyafaz-ep/partner`.",
        ],
      },
      {
        heading: "Fixed",
        items: [
          "Logging into one HQ portal no longer replaces another portal’s session.",
          "Kiosk cookie preferred when portal header is absent so HQ cookies do not steal kiosk.",
          "Salon package boot: `admin_shared.database` env_config import fallback.",
        ],
      },
    ],
  },
  {
    version: "1.1",
    date: "2026-08-12",
    title: "Portal analytics & AI chat",
    sections: [
      {
        heading: "Added",
        items: [
          "Nyafaz Portal organization analytics.",
          "Kiosk landing show/hide for all content blocks.",
          "Reusable AI chat UI and Workspace AI Chat settings with RAG knowledge files.",
        ],
      },
      {
        heading: "Fixed",
        items: [
          "Kiosk landing logo scale on builder preview and live landings.",
        ],
      },
    ],
  },
  {
    version: "1.0",
    date: "2026-08-12",
    title: "Production baseline",
    sections: [
      {
        heading: "Added",
        items: [
          "Official production baseline for the Naturals franchise stack.",
          "HQ-network Face Identity across franchises under the same organisation.",
          "Encrypted face embeddings and guest PII; cookie-primary HQ auth.",
          "DPDP consent, export, and erase.",
        ],
      },
      {
        heading: "Security",
        items: [
          "Audit score 93 / 100 with no open Critical / High / Medium findings.",
        ],
      },
    ],
  },
];
